/**
 * PricingConfigurator — the AWS-calculator-style panel that lives on
 * service detail pages.
 *
 * Contract (all fields come from the API response as-is):
 *   service.configurator.fields[]       — inputs the customer can tune
 *   service.default_configuration       — initial breakdown (on first paint)
 *   service.how_it_scales.markdown      — optional explainer
 *
 * When the customer changes a field we POST to /api/pricing/estimate
 * with { service_id, config } and re-render the breakdown from the
 * response. The backend is the single source of truth; this component
 * does ZERO pricing math.
 *
 * Pricing-model-agnostic. Every pricing model the presenter supports
 * produces a configurator.fields[] + a default_configuration with the
 * same shape, so one component handles all of them. If a service does
 * not emit a configurator the caller should not render this component.
 *
 * PR-10 part 2.
 */

import {
  Box,
  Chip,
  CircularProgress,
  Collapse,
  FormControl,
  FormControlLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Typography,
  useTheme,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/CheckCircle';
import React from 'react';
import ReactMarkdown from 'react-markdown';

// ---------- wire types (we keep these loose on purpose; the server is the contract) ----------

interface ConfiguratorNumberField {
  key: string;
  kind: 'number';
  label: string;
  helperText?: string;
  min: number;
  max: number;
  step?: number;
  default: number;
}

interface ConfiguratorRadioField {
  key: string;
  kind: 'radio';
  label: string;
  helperText?: string;
  options: Array<{ value: string | number; label: string; caption?: string }>;
  default: string | number;
}

type ConfiguratorField = ConfiguratorNumberField | ConfiguratorRadioField;

interface BreakdownRow {
  label: string;
  amount: string;
  cents: number;
}

interface DefaultConfiguration {
  summary: string;
  monthlyBreakdown: BreakdownRow[];
  setupBreakdown: BreakdownRow[];
  monthlyTotal: string;
  setupTotal: string;
  monthlyTotalCents: number;
  setupTotalCents: number;
  configId: string;
}

interface HowItScales {
  headline: string;
  markdown: string;
}

export interface PricingConfiguratorProps {
  serviceId: string;
  configurator: { fields: ConfiguratorField[] };
  /** Initial breakdown shown before the user changes anything. */
  initialConfiguration: DefaultConfiguration;
  howItScales?: HowItScales;
  /** Debounce window for estimate calls. 300ms feels responsive without hammering. */
  debounceMs?: number;
  /** Override the estimate endpoint for tests / storybook. */
  estimateEndpoint?: string;
  className?: string;
}

// ---------- internal state ----------

type ConfigValue = number | string | boolean;
type ConfigMap = Record<string, ConfigValue>;

function initialConfigMap(fields: ConfiguratorField[]): ConfigMap {
  const out: ConfigMap = {};
  for (const f of fields) out[f.key] = f.default;
  return out;
}

// ---------- breakdown subcomponent ----------

interface BreakdownSectionProps {
  title: string;
  rows: BreakdownRow[];
  total: string;
  totalLabel: string;
  dimmed: boolean;
}

const BreakdownSection: React.FC<BreakdownSectionProps> = ({
  title,
  rows,
  total,
  totalLabel,
  dimmed,
}) => {
  if (!rows || rows.length === 0) return null;
  return (
    <Box sx={{ opacity: dimmed ? 0.55 : 1, transition: 'opacity 120ms ease-out' }}>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', fontWeight: 700, letterSpacing: 0.5 }}
      >
        {title}
      </Typography>
      {rows.map((row, i) => (
        <Box
          key={`${title}-${i}`}
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'baseline',
            gap: 2,
            py: 0.5,
          }}
        >
          <Typography variant="body2" sx={{ color: 'text.primary' }}>
            {row.label}
          </Typography>
          <Typography
            variant="body2"
            sx={{ color: 'text.primary', whiteSpace: 'nowrap' }}
          >
            {row.amount}
          </Typography>
        </Box>
      ))}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'baseline',
          gap: 2,
          pt: 1,
          mt: 0.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="body2" sx={{ fontWeight: 700 }}>
          {totalLabel}
        </Typography>
        <Typography variant="body2" sx={{ fontWeight: 700, whiteSpace: 'nowrap' }}>
          {total}
        </Typography>
      </Box>
    </Box>
  );
};

// ---------- main component ----------

export const PricingConfigurator: React.FC<PricingConfiguratorProps> = ({
  serviceId,
  configurator,
  initialConfiguration,
  howItScales,
  debounceMs = 300,
  estimateEndpoint = '/api/pricing/estimate',
  className,
}) => {
  const theme = useTheme();
  const fields = configurator.fields;

  const [config, setConfig] = React.useState<ConfigMap>(() => initialConfigMap(fields));
  const [live, setLive] = React.useState<DefaultConfiguration>(initialConfiguration);
  const [pending, setPending] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [howExpanded, setHowExpanded] = React.useState(false);

  // Each field change replaces the config map and schedules a debounced
  // estimate call. We track a token so a stale response never overwrites
  // a fresher one (network races during fast stepper clicks).
  const reqTokenRef = React.useRef(0);
  const debounceTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);

  const fetchEstimate = React.useCallback(
    async (next: ConfigMap) => {
      const myToken = ++reqTokenRef.current;
      setPending(true);
      setError(null);
      try {
        const res = await fetch(estimateEndpoint, {
          method: 'POST',
          headers: { 'content-type': 'application/json' },
          body: JSON.stringify({ service_id: serviceId, config: next }),
        });
        if (!res.ok) {
          const text = await res.text().catch(() => '');
          throw new Error(`estimate ${res.status}${text ? `: ${text}` : ''}`);
        }
        const json = await res.json();
        if (reqTokenRef.current !== myToken) return; // stale
        const nextLive: DefaultConfiguration = {
          summary: json.summary,
          monthlyBreakdown: json.breakdown || [],
          setupBreakdown: json.setupBreakdown || [],
          monthlyTotal: json.monthlyTotal,
          setupTotal: json.setupTotal,
          monthlyTotalCents: json.monthlyTotalCents,
          setupTotalCents: json.setupTotalCents,
          configId: json.configId,
        };
        setLive(nextLive);
      } catch (err) {
        if (reqTokenRef.current !== myToken) return;
        setError('Pricing estimate unavailable. Showing last-known totals.');
        // eslint-disable-next-line no-console
        console.warn('[PricingConfigurator] estimate failed:', err);
      } finally {
        if (reqTokenRef.current === myToken) setPending(false);
      }
    },
    [serviceId, estimateEndpoint],
  );

  const scheduleEstimate = React.useCallback(
    (next: ConfigMap) => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
      debounceTimerRef.current = setTimeout(() => {
        fetchEstimate(next);
      }, debounceMs);
    },
    [debounceMs, fetchEstimate],
  );

  React.useEffect(() => {
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
  }, []);

  const handleChange = (key: string, value: ConfigValue) => {
    const next = { ...config, [key]: value };
    setConfig(next);
    scheduleEstimate(next);
  };

  const renderField = (field: ConfiguratorField) => {
    if (field.kind === 'number') {
      const current = Number(config[field.key] ?? field.default);
      const clamp = (n: number) => Math.max(field.min, Math.min(field.max, n));
      const step = field.step ?? 1;
      return (
        <Box key={field.key} sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
            {field.label}
          </Typography>
          {field.helperText && (
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: 'block', mb: 0.75 }}
            >
              {field.helperText}
            </Typography>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              size="small"
              aria-label={`decrease ${field.label}`}
              disabled={current <= field.min}
              onClick={() => handleChange(field.key, clamp(current - step))}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                width: 32,
                height: 32,
              }}
            >
              <RemoveIcon fontSize="small" />
            </IconButton>
            <TextField
              size="small"
              type="number"
              value={current}
              onChange={(e) => {
                const raw = parseInt(e.target.value, 10);
                if (!Number.isFinite(raw)) return;
                handleChange(field.key, clamp(raw));
              }}
              inputProps={{
                min: field.min,
                max: field.max,
                step,
                style: { textAlign: 'center', width: 70 },
                'aria-label': field.label,
              }}
              sx={{ width: 90 }}
            />
            <IconButton
              size="small"
              aria-label={`increase ${field.label}`}
              disabled={current >= field.max}
              onClick={() => handleChange(field.key, clamp(current + step))}
              sx={{
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 1,
                width: 32,
                height: 32,
              }}
            >
              <AddIcon fontSize="small" />
            </IconButton>
            <Typography variant="caption" color="text.secondary" sx={{ ml: 1 }}>
              Range: {field.min}–{field.max.toLocaleString()}
            </Typography>
          </Box>
        </Box>
      );
    }

    // radio
    const current = String(config[field.key] ?? field.default);
    return (
      <Box key={field.key} sx={{ mb: 2.5 }}>
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
          {field.label}
        </Typography>
        {field.helperText && (
          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: 'block', mb: 0.75 }}
          >
            {field.helperText}
          </Typography>
        )}
        <FormControl>
          <RadioGroup
            row
            value={current}
            onChange={(e) => {
              const raw = e.target.value;
              // Preserve number vs string distinction from the field's
              // option values. This matters because the backend receives
              // term_months as a number but install as a string.
              const matched = field.options.find((o) => String(o.value) === raw);
              handleChange(field.key, matched ? matched.value : raw);
            }}
          >
            {field.options.map((opt) => (
              <FormControlLabel
                key={String(opt.value)}
                value={String(opt.value)}
                control={<Radio size="small" />}
                label={
                  <Box>
                    <Typography variant="body2">{opt.label}</Typography>
                    {opt.caption && (
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        sx={{ display: 'block', mt: -0.25 }}
                      >
                        {opt.caption}
                      </Typography>
                    )}
                  </Box>
                }
                sx={{ mr: 3, alignItems: 'flex-start' }}
              />
            ))}
          </RadioGroup>
        </FormControl>
      </Box>
    );
  };

  return (
    <Box
      className={className}
      sx={{
        mb: 4,
        p: 3,
        bgcolor: 'background.paper',
        borderRadius: 2,
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        variant="h6"
        gutterBottom
        sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
      >
        <CheckIcon color="success" />
        Configure your order
      </Typography>

      <Box sx={{ mb: 2 }}>
        {fields.map(renderField)}
      </Box>

      {/* Summary chip + total */}
      <Box
        sx={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 2,
          mb: 2,
          flexWrap: 'wrap',
        }}
      >
        <Typography
          variant="h4"
          sx={{
            fontWeight: 'bold',
            color: 'primary.main',
            opacity: pending ? 0.55 : 1,
            transition: 'opacity 120ms ease-out',
          }}
        >
          {live.monthlyTotal}
        </Typography>
        <Chip
          size="small"
          label={live.summary}
          variant="outlined"
          sx={{ borderColor: theme.palette.divider }}
        />
        {pending && <CircularProgress size={16} sx={{ ml: 0.5 }} />}
      </Box>

      {error && (
        <Typography
          variant="caption"
          color="warning.main"
          sx={{ display: 'block', mb: 1 }}
        >
          {error}
        </Typography>
      )}

      {/* Breakdown */}
      <Box
        sx={{
          p: 2,
          borderRadius: 1.5,
          bgcolor: theme.palette.grey[50],
          border: '1px solid',
          borderColor: 'divider',
          mb: howItScales ? 2 : 0,
        }}
      >
        <BreakdownSection
          title="Monthly"
          rows={live.monthlyBreakdown}
          total={live.monthlyTotal}
          totalLabel="Monthly total"
          dimmed={pending}
        />
        {live.setupBreakdown && live.setupBreakdown.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <BreakdownSection
              title="One-time"
              rows={live.setupBreakdown}
              total={live.setupTotal}
              totalLabel="One-time total"
              dimmed={pending}
            />
          </Box>
        )}
      </Box>

      {/* How this scales */}
      {howItScales && (
        <Box
          sx={{
            borderRadius: 1.5,
            border: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          <Box
            onClick={() => setHowExpanded((v) => !v)}
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              px: 2,
              py: 1.5,
              cursor: 'pointer',
              bgcolor: howExpanded ? 'action.hover' : 'transparent',
              userSelect: 'none',
              '&:hover': { bgcolor: 'action.hover' },
            }}
            role="button"
            aria-expanded={howExpanded}
            aria-label="Toggle how this pricing scales"
          >
            <Typography variant="body2" sx={{ fontWeight: 600 }}>
              How this scales
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="caption" color="text.secondary">
                {howItScales.headline}
              </Typography>
              <ExpandMoreIcon
                fontSize="small"
                sx={{
                  transition: 'transform 160ms ease',
                  transform: howExpanded ? 'rotate(180deg)' : 'rotate(0)',
                }}
              />
            </Box>
          </Box>
          <Collapse in={howExpanded} unmountOnExit>
            <Box
              sx={{
                px: 2,
                py: 1.5,
                borderTop: '1px solid',
                borderColor: 'divider',
                fontSize: '0.875rem',
                '& table': {
                  borderCollapse: 'collapse',
                  width: '100%',
                  my: 1,
                  fontSize: '0.85rem',
                },
                '& th, & td': {
                  border: '1px solid',
                  borderColor: 'divider',
                  px: 1,
                  py: 0.5,
                  textAlign: 'left',
                },
                '& th': { fontWeight: 700, bgcolor: theme.palette.grey[50] },
                '& p': { my: 0.5 },
              }}
            >
              <ReactMarkdown>{howItScales.markdown}</ReactMarkdown>
            </Box>
          </Collapse>
        </Box>
      )}
    </Box>
  );
};

export default PricingConfigurator;
